/**
 * Interview Station Results Component
 *
 * Displays complete interview analysis results:
 * - Sentiment Dashboard (emotion pulse + FAIR heatmap)
 * - Recommendations list
 * - Interview metadata
 *
 * Sprint 7 - Interview Enhancement
 */

import React, { useEffect, useState } from 'react';
import { SentimentDashboard } from '../SentimentDashboard';
import '../../styles/station-results.css';

interface InterviewResultsProps {
  jobId: string;
  journeyId?: string;
}

// Recommendation type matching the API response
interface InterviewRecommendation {
  id?: string;
  priority?: string;
  fair_pillar?: string;
  title?: string;
  description?: string;
  effort?: string;
  impact_score?: number;
}

// Sentiment types matching SentimentDashboard expectations
interface EmotionScore {
  emotion: string;
  percentage: number;
  count: number;
}

interface SentimentQuote {
  text: string;
  sentiment: number;
}

interface FairPillarSentiment {
  overall_sentiment: number;
  by_persona: Record<string, number>;
  quotes: SentimentQuote[];
}

interface SentimentAnalysis {
  emotions: {
    overall_emotion: string;
    emotion_scores: Record<string, number>;
    emotion_distribution: EmotionScore[];
  };
  fair_heatmap: {
    by_pillar: Record<string, FairPillarSentiment>;
  };
  insights: {
    biggest_pain_point: string;
    biggest_win: string;
    emotional_intensity: number;
    consensus_level: number;
  };
  metadata: {
    total_statements: number;
    analyzed_at: string;
    model: string;
  };
}

interface InterviewResult {
  analysis_job_id: string;
  interview_ids: string[];
  total_interviews: number;
  total_statements: number;
  recommendations: InterviewRecommendation[];
  sentiment_analysis: SentimentAnalysis;
  processing_time_ms: number;
  completed_at: string;
}

export function InterviewResults({ jobId, journeyId }: InterviewResultsProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InterviewResult | null>(null);

  useEffect(() => {
    loadResults();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadResults is defined in component
  }, [jobId]);

  const loadResults = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch journey station results
      const response = await fetch(`/api/journey/${journeyId}/station/interviews`);

      if (!response.ok) {
        throw new Error(`Failed to load results: ${response.statusText}`);
      }

      const data = await response.json();

      // Extract interview results from station data
      if (data.results?.data) {
        setResult(data.results.data);
      } else {
        throw new Error('No results data found');
      }

    } catch (err: unknown) {
      console.error('[InterviewResults] Error loading results:', err);
      setError(err instanceof Error ? err.message : 'Failed to load interview results');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="station-results-loading">
        <div className="spinner" />
        <p>Loading interview analysis results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="station-results-error">
        <h3>⚠️ Error Loading Results</h3>
        <p>{error}</p>
        <button onClick={loadResults} className="btn-retry">
          Retry
        </button>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="station-results-error">
        <h3>⚠️ No Results Available</h3>
        <p>No interview analysis results found for job ID: {jobId}</p>
      </div>
    );
  }

  return (
    <div className="station-results interview-results">
      {/* Header */}
      <div className="station-results__header">
        <div className="station-results__title">
          <span className="station-results__icon">🎤</span>
          <h2>Interview Analysis Results</h2>
        </div>
        <div className="station-results__meta">
          <div className="meta-item">
            <span className="meta-label">Interviews:</span>
            <span className="meta-value">{result.total_interviews}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Statements:</span>
            <span className="meta-value">{result.total_statements}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Recommendations:</span>
            <span className="meta-value">{result.recommendations?.length || 0}</span>
          </div>
        </div>
      </div>

      {/* Sentiment Dashboard (Primary Feature) */}
      {result.sentiment_analysis && (
        <div className="station-results__section">
          <SentimentDashboard sentiment={result.sentiment_analysis} />
        </div>
      )}

      {/* Recommendations */}
      {result.recommendations && result.recommendations.length > 0 && (
        <div className="station-results__section">
          <h3>💡 Recommendations</h3>
          <div className="recommendations-list">
            {result.recommendations.map((rec, index) => (
              <div key={rec.id ?? `rec-${index}`} className="recommendation-card">
                <div className="recommendation-header">
                  <span className={`recommendation-priority recommendation-priority--${rec.priority ?? 'medium'}`}>
                    {rec.priority?.toUpperCase() ?? 'MEDIUM'}
                  </span>
                  <span className="recommendation-pillar">{rec.fair_pillar ?? 'General'}</span>
                </div>
                <h4 className="recommendation-title">{rec.title ?? 'Recommendation'}</h4>
                <p className="recommendation-description">{rec.description ?? ''}</p>
                <div className="recommendation-footer">
                  <span className="recommendation-effort">
                    Effort: {rec.effort ?? 'N/A'}
                  </span>
                  <span className="recommendation-impact">
                    Impact: {rec.impact_score?.toFixed(1) ?? 'N/A'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Interview Metadata */}
      <div className="station-results__section station-results__metadata">
        <h3>📋 Analysis Details</h3>
        <div className="metadata-grid">
          <div className="metadata-item">
            <span className="metadata-label">Analysis Job ID:</span>
            <span className="metadata-value">{result.analysis_job_id}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Completed:</span>
            <span className="metadata-value">
              {new Date(result.completed_at).toLocaleString()}
            </span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Processing Time:</span>
            <span className="metadata-value">
              {(result.processing_time_ms / 1000).toFixed(2)}s
            </span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Interview IDs:</span>
            <span className="metadata-value">
              {result.interview_ids?.join(', ') || 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
